import {useCallback, useMemo} from 'preact/hooks';

import {BrandImage} from 'components';
import {BrandHandle} from 'utilities/delivery-promise';
import {Scenario} from 'utilities/promise-presenter';
import {Line, Slot, UiLineType} from 'layouts';
import {Theme} from 'utilities/color';
import {JSXNode} from 'utilities/props';
import {ReportType, useErrorLogger} from 'utilities/error-logger';
import {useI18n} from 'utilities/i18n';

import styles from '../../layouts/components/AmazonLayout/AmazonLayout.css';

import {InfoTooltipIcon} from './media';
import {OptimusButton} from './components';

interface Props {
  promisePresentment: Scenario;
  theme: Theme;
}

export default function BuyWithPrimeBrand({promisePresentment, theme}: Props) {
  const errorLogger = useErrorLogger();
  const {translate} = useI18n();

  const handleShowLearnMoreInfo = useCallback(() => {
    if (
      promisePresentment.brandHandle === BrandHandle.Prime &&
      typeof window.BuyWithPrime.showLearnMore === 'function'
    ) {
      try {
        window.BuyWithPrime.showLearnMore(
          theme === Theme.Dark ? 'dark' : 'light',
        );
      } catch (err) {
        errorLogger.reportError({
          severity: ReportType.Investigate,
          error: err,
          message:
            '[ShopPromise] Failed to open Buy with Prime learn more modal',
        });
      }
    }
  }, [errorLogger, promisePresentment.brandHandle, theme]);

  const contentMarkup = useMemo(
    () => (
      <>
        <Line type={UiLineType.Title}>
          {updatedTitle(promisePresentment.title)}
        </Line>
        <Line type={UiLineType.MarketingMessage}>
          {promisePresentment.marketingMessage ?? ''}
        </Line>
        <Line type={UiLineType.Destination}>
          {promisePresentment.locationPrefix}
        </Line>
      </>
    ),
    [
      promisePresentment.locationPrefix,
      promisePresentment.marketingMessage,
      promisePresentment.title,
    ],
  );

  const brandMarkup = useMemo(
    () => (
      <Line type={UiLineType.BrandImage}>
        <BrandImage
          isVisible={false}
          onClick={handleShowLearnMoreInfo}
          logoSrc={promisePresentment.logoSrc}
          logoAltText={promisePresentment.logoAltText}
          brandHandle={promisePresentment.brandHandle}
        />
      </Line>
    ),
    [
      handleShowLearnMoreInfo,
      promisePresentment.brandHandle,
      promisePresentment.logoAltText,
      promisePresentment.logoSrc,
    ],
  );

  const learnMore = useMemo(() => {
    return (
      <Line type={UiLineType.Generic}>
        <span className={styles.nowrap}>
          {getLastWord(promisePresentment.title)}{' '}
          <button
            className={styles.icon}
            onClick={handleShowLearnMoreInfo}
            type="button"
          >
            <img
              className={styles.iconImg}
              src={InfoTooltipIcon}
              alt={translate('buyWithPrime.learnMore')}
            />
          </button>
        </span>
      </Line>
    );
  }, [promisePresentment.title, handleShowLearnMoreInfo, translate]);

  const genericMarkup = useMemo(
    () => (
      <Line type={UiLineType.FullSize}>
        <OptimusButton />
      </Line>
    ),
    [],
  );

  return (
    <>
      <Slot name="Content">{contentMarkup}</Slot>
      <Slot name="Brand">{brandMarkup}</Slot>
      <Slot name="LearnMore">{learnMore}</Slot>
      {genericMarkup}
    </>
  );
}

function updatedTitle(title: string): JSXNode {
  const extractedDate = extractDateFromContent(title);
  const beforeDate = title.substring(0, title.indexOf(extractedDate));
  const afterDate = title.substring(
    title.indexOf(extractedDate) + extractedDate.length,
  );

  const formattedTitle: JSXNode = (
    <>
      {beforeDate}
      <span class={styles.nowrap}>{extractedDate}</span>
      {exceptLastWord(afterDate)}
    </>
  );

  return formattedTitle;
}

const dateRegexp = new RegExp(
  '(((?:Today|Tomorrow|Mon|Tues|Wed|Thurs|Fri|Sat|Sun).*?,?\\s)?(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sept|Oct|Nov|Dec).*?\\s?\\d{1,2})',
  'gi',
);

function extractDateFromContent(str: string) {
  const match = str.match(dateRegexp);
  if (match) {
    return match[0];
  }
  return '';
}

function getLastWord(str: string) {
  const words = str.split(' ');
  return words[words.length - 1];
}

function exceptLastWord(str: string) {
  const words = str.split(' ');
  if (words.length > 1) {
    words.pop();
  }
  return words.join(' ');
}
